import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { uploadProjectFile, ProjectFile } from '../../services/Project';
import { useI18n } from '../../i18n';
import {
  MAX_FILES_PER_UPLOAD,
  MAX_PROJECT_FILE_MB,
  UPLOAD_CONCURRENCY,
  runWithConcurrency,
  screenFiles,
  type ScreenedFiles,
  type SkipReason,
} from './project-uploads';

export type UploadStatus = 'waiting' | 'uploading' | 'uploaded' | 'failed';

export interface UploadEntry {
  key: string;
  name: string;
  size: number;
  status: UploadStatus;
  /** 0-100 */
  progress: number;
  error?: string;
}

export interface UploadOutcome {
  uploaded: ProjectFile[];
  failed: File[];
}

const NOTHING: UploadOutcome = { uploaded: [], failed: [] };

/** Names for a toast's description, cut short when there are many */
const listNames = (files: File[], max = 5): string => {
  const names = files.slice(0, max).map((f) => f.name);
  return files.length > max ? `${names.join(', ')} +${files.length - max}` : names.join(', ');
};

/** "<reason>: <names>" for each reason, so the toast says why each file failed or was skipped */
const describeByReason = <R>(
  items: { file: File; reason: R }[],
  label: (reason: R) => string,
): string => {
  const byReason = new Map<R, File[]>();
  for (const { file, reason } of items) {
    byReason.set(reason, [...(byReason.get(reason) ?? []), file]);
  }
  return Array.from(byReason, ([reason, files]) => `${label(reason)}: ${listNames(files)}`).join(
    ' · ',
  );
};

/**
 * Whether trying again could help: not for a request the server turned down
 * (too large, no access, bad file), but yes for a server error, a timeout, a
 * rate limit or a dropped connection (no status).
 */
const isRetryable = (status?: number): boolean =>
  status === undefined || status >= 500 || status === 408 || status === 429;

/**
 * Uploads a batch of files to a project, a few at a time, and keeps each
 * file's state for the progress dialog. One toast sums up the batch, with a
 * retry for the files that failed when the caller can take them again.
 */
export function useProjectFileUploads() {
  const { t } = useI18n();
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // State lags a render behind; this stops a second drop starting a batch alongside
  const busy = useRef(false);

  /** "Skipped (over 50 MB): big.pdf · …", for a toast's description */
  const describeSkipped = useCallback(
    (skipped: ScreenedFiles['skipped']): string => {
      const r = t.projectUploads.skippedReason;
      const labels: Record<SkipReason, string> = {
        type: r.type,
        size: r.size(MAX_PROJECT_FILE_MB),
        empty: r.empty,
        count: r.count(MAX_FILES_PER_UPLOAD),
      };
      return describeByReason(skipped, (reason) => labels[reason]);
    },
    [t],
  );

  /**
   * Splits picked or dropped files into the ones to upload and the ones to
   * skip. Pass `skipped` on to `upload`, whose summary toast names them; when
   * nothing is left to upload, this says so itself, in one toast.
   */
  const screen = useCallback(
    (files: ArrayLike<File>): ScreenedFiles => {
      if (busy.current) {
        toast.info(t.projectUploads.toasts.busy);
        return { accepted: [], skipped: [] };
      }
      const screened = screenFiles(files);
      const { accepted, skipped } = screened;
      if (accepted.length === 0 && skipped.length > 0) {
        const tt = t.projectUploads.toasts;
        const reasons = new Set(skipped.map((s) => s.reason));
        if (reasons.size === 1) {
          const titles: Record<SkipReason, string> = {
            type: tt.skippedType,
            size: tt.skippedSize(MAX_PROJECT_FILE_MB),
            empty: tt.skippedEmpty,
            count: tt.skippedCount(MAX_FILES_PER_UPLOAD),
          };
          toast.warning(titles[skipped[0].reason], {
            description: listNames(skipped.map((s) => s.file)),
          });
        } else {
          toast.warning(tt.noneAdded, { description: describeSkipped(skipped) });
        }
      }
      return screened;
    },
    [t, describeSkipped],
  );

  /**
   * Uploads already-screened files. `skipped` (from `screen`) is named in the
   * summary toast, so it can't end up stacked out of sight behind it.
   * `onRetry` gets the failed files when the user clicks Retry on the summary
   * toast; leave it out where the caller can't take them again (e.g. it
   * navigates away).
   */
  const upload = useCallback(
    async (
      projectId: string,
      files: File[],
      options: { skipped?: ScreenedFiles['skipped']; onRetry?: (failed: File[]) => void } = {},
    ): Promise<UploadOutcome> => {
      if (files.length === 0) return NOTHING;
      if (busy.current) {
        toast.info(t.projectUploads.toasts.busy);
        return NOTHING;
      }
      busy.current = true;

      const batchId = Date.now();
      const keys = files.map((_, i) => `${batchId}-${i}`);
      const patch = (key: string, changes: Partial<UploadEntry>) =>
        setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...changes } : e)));

      setEntries(
        files.map((file, i) => ({
          key: keys[i],
          name: file.name,
          size: file.size,
          status: 'waiting',
          progress: 0,
        })),
      );
      setIsUploading(true);

      try {
        type Result = { file: ProjectFile } | { error: string; retryable: boolean };
        const results = await runWithConcurrency<File, Result>(
          files,
          UPLOAD_CONCURRENCY,
          async (file, i) => {
            const key = keys[i];
            patch(key, { status: 'uploading' });
            let error = t.projectUploads.errors.generic;
            let status: number | undefined;
            try {
              const res = await uploadProjectFile(projectId, file, (progress) =>
                patch(key, { progress }),
              );
              if (res.success && res.data) {
                patch(key, { status: 'uploaded', progress: 100 });
                return { file: res.data };
              }
              status = res.status;
              // The backend's limit. The ingress's own 413 never gets here
              // (no CORS headers): screenFiles keeps files under it.
              if (res.status === 413) {
                error = t.projectUploads.errors.tooLarge(MAX_PROJECT_FILE_MB);
              } else if (res.status !== undefined && res.errors?.[0]) {
                // The server's own words; without a response, errors only
                // holds the service's English fallback
                error = res.errors[0].detail || res.errors[0].title || error;
              }
            } catch (err) {
              console.error('[useProjectFileUploads] Upload error:', err);
            }
            patch(key, { status: 'failed', error });
            return { error, retryable: isRetryable(status) };
          },
        );

        const uploaded: ProjectFile[] = [];
        const failures: { file: File; error: string; retryable: boolean }[] = [];
        results.forEach((r, i) => {
          if ('file' in r) uploaded.push(r.file);
          else failures.push({ file: files[i], ...r });
        });
        const failed = failures.map((f) => f.file);

        const { skipped = [], onRetry } = options;
        const skippedNote = skipped.length > 0 ? describeSkipped(skipped) : '';
        if (failed.length === 0) {
          const title =
            uploaded.length === 1
              ? t.projectUploads.toasts.uploadedOne(files[0].name)
              : t.projectUploads.toasts.uploadedMany(uploaded.length);
          if (skippedNote) toast.success(title, { description: skippedNote });
          else toast.success(title);
        } else {
          const retryable = failures.filter((f) => f.retryable).map((f) => f.file);
          const failedNote =
            files.length === 1
              ? failures[0].error
              : describeByReason(
                  failures.map((f) => ({ file: f.file, reason: f.error })),
                  (error) => error,
                );
          toast.error(
            files.length === 1
              ? t.projectUploads.toasts.failedOne(failed[0].name)
              : t.projectUploads.toasts.failedSome(failed.length, files.length),
            {
              description: [failedNote, skippedNote].filter(Boolean).join(' · '),
              action:
                onRetry && retryable.length > 0
                  ? { label: t.common.actions.retry, onClick: () => onRetry(retryable) }
                  : undefined,
            },
          );
        }
        return { uploaded, failed };
      } finally {
        busy.current = false;
        setIsUploading(false);
      }
    },
    [t, describeSkipped],
  );

  return { entries, isUploading, screen, upload };
}
